import { useKeycloakToken } from 'shared/auth/useKeycloak';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getConnectionsList } from 'connections/api/connectionsApi';
import { type IntegrationRoles, loadIntegrationRoleMap } from 'routes/api';
import ConnectionsList from './ConnectionsList';
import { Connection } from 'shared/types/ObjectTypes';
import { ManageConnectionDetails } from './ManageConnectionDetails';
import { useAllOrganizations, getUserPermissions } from 'workspaces/api';
import { page_size } from 'shared/api/apiUtils';
import {
  useDestinationTypes,
  useDestinationUrls,
} from 'shared/api/useFilterOptions';
import { useListQuery } from 'shared/api/useListQuery';
import { queryKeys } from 'shared/api/queryKeys';
import { useKeycloak } from '@react-keycloak/web';
import { useHelp } from 'shared/context/HelpContext';
import { useTranslation } from 'react-i18next';

const ConnectionsPage = () => {
  const { t, i18n } = useTranslation();
  const { setHelpContent } = useHelp();

  useEffect(() => {
    setHelpContent({
      cards: [
        {
          title: t('Managing Connections in Gundi'),
          description: t(
            'Learn how to set up the systems Gundi exchanges data with, as sources or as destinations.',
          ),
          button: {
            label: t('Learn More'),
            url: 'https://support.earthranger.com/connections-gundi/connections-in-gundi',
          },
        },
      ],
    });
  }, [i18n.language, t, setHelpContent]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);
  type FilterTypes = {
    base_url__in: FilterTags[];
    type__in: FilterTags[];
    owner__in: FilterTags[];
    ordering: string;
    search: string;
  };
  type FilterTags = {
    name: string;
    id: string;
  };
  const defaultConnectionsFilter = {
    base_url__in: [],
    type__in: [],
    owner__in: [],
    ordering: 'name',
    search: '',
  };
  const navigate = useNavigate();
  const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
  const { keycloak } = useKeycloak();
  const [canAddConnection, setCanAddConnection] = useState(false);
  // Which Routes use each record, and in which role. No endpoint reports this
  // per integration, so it is derived once from the Routes list and reused for
  // every row. The sweep walks every Route, so it is reused for a few minutes
  // across visits; any Route or connection save marks it out of date at once.
  const roleMapQuery = useQuery({
    queryKey: queryKeys.connections.roleMap(),
    queryFn: () => loadIntegrationRoleMap(token as string),
    staleTime: 5 * 60 * 1000,
    enabled: !!token && typeof token === 'string' && !keycloak.isTokenExpired(),
  });
  const roleMap: Record<string, IntegrationRoles> = roleMapQuery.data ?? {};
  // Until the sweep lands, a record's roles are unknown — which is not the same as
  // "not in use", so the chips wait rather than claiming something false.
  const roleMapLoaded = roleMapQuery.data !== undefined;
  const [roleFilter, setRoleFilter] = useState<{
    label: string;
    value: string;
  } | null>(null);
  // The ids a role resolves to, from the Routes sweep. null = no role filter.
  const roleFilterIds: string[] | null = !roleFilter
    ? null
    : Object.keys(roleMap).filter((id) => {
        const roles = roleMap[id];
        return roleFilter.value === 'source'
          ? roles.asProvider.length > 0
          : roles.asDestination.length > 0;
      });
  const [connectionFilter, setConnectionFilter] = useState<FilterTypes>(() => {
    const savedFilter = sessionStorage.getItem('destinationFilter');
    return savedFilter
      ? { ...defaultConnectionsFilter, ...JSON.parse(savedFilter) }
      : defaultConnectionsFilter;
  });
  const [offset, setOffset] = useState<number>(0);
  const { organizations, loadingOrganizations } = useAllOrganizations();
  const {
    destinationTypes: connectionTypes,
    loadingDestinationTypes: loadingConnectionTypes,
  } = useDestinationTypes({
    allTypes: true,
  });
  const {
    destinationUrls: connectionUrls,
    loadingDestinationUrls: loadingConnectionUrls,
  } = useDestinationUrls();
  const [connectionDetailsModal, setConnectionDetailsModal] = useState(false);

  const connectionFilterQuery = useMemo(
    () => ({
      base_url__in: connectionFilter.base_url__in.map((a) => a.id).toString(),
      type__in: connectionFilter.type__in.map((a) => a.id).toString(),
      owner__in: connectionFilter.owner__in.map((a) => a.id).toString(),
      ordering: connectionFilter.ordering,
      search: connectionFilter.search,
      offset: offset,
      previous:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/integrations/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (offset - page_size),
      next:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/integrations/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (offset + page_size),
      // Role is not a field the API filters on, but it resolves to a set of
      // ids the Routes sweep already gave us — pushed down as id__in so
      // the server pages the real result set instead of the browser hiding
      // rows from one page.
      ...(roleFilterIds === null ? {} : { id__in: roleFilterIds.join(',') }),
    }),
    [connectionFilter, offset, roleFilterIds],
  );
  const connectionsList = useListQuery<Connection>(
    queryKeys.connections.list(connectionFilterQuery),
    // No ids means no matches, so don't ask.
    async (token) =>
      roleFilterIds !== null && roleFilterIds.length === 0
        ? { results: [], count: 0 }
        : getConnectionsList(token, connectionFilterQuery),
  );
  // Settles on the first answer even if it failed, so the page never hangs on
  // its skeleton.
  const pageLoaded = connectionsList.settled;

  useEffect(() => {
    sessionStorage.setItem(
      'destinationFilter',
      JSON.stringify(connectionFilter),
    );
  }, [connectionFilter]);

  useEffect(() => {
    const loadPerms = async () => {
      if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
        try {
          const temp = await getUserPermissions(token);
          setCanAddConnection(temp);
        } catch (err) {
          console.log(err);
        }
      }
    };
    loadPerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAddConnectionPage = () => {
    navigate('/connections/new');
  };

  const handleToggleConnectionDetailsModal = async (dest_id?: string) => {
    if (
      dest_id &&
      token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired()
    ) {
      navigate(`/connections/${dest_id}/configuration`, { replace: false });
    }
    setConnectionDetailsModal(!connectionDetailsModal);
    if (connectionDetailsModal) connectionsList.refresh();
  };

  const handleGlobalSearch = (searchSubStr: string) => {
    setConnectionFilter((prev) => ({ ...prev, search: searchSubStr }));
    setOffset(0);
  };

  const handleFilterEndpoint = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setConnectionFilter((prev) => ({ ...prev, base_url__in: tags }));
    setOffset(0);
  };

  const handleFilterType = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setConnectionFilter((prev) => ({ ...prev, type__in: tags }));
    setOffset(0);
  };

  const handleFilterOwner = (selected: any) => {
    const tags: FilterTags[] = selected
      ? [{ name: selected.label, id: selected.value }]
      : [];
    setConnectionFilter((prev) => ({ ...prev, owner__in: tags }));
    setOffset(0);
  };

  const handleSortBy = (selected: { label: string; value: string }) => {
    setConnectionFilter((prev) => ({ ...prev, ordering: selected.value }));
    setOffset(0);
  };

  const handlePageClick = (e: any) => {
    setOffset(e.selected * page_size);
  };

  const toSingleValue = (tags: FilterTags[]) =>
    tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

  const endpointSelectValue = toSingleValue(connectionFilter.base_url__in);
  const typeSelectValue = toSingleValue(connectionFilter.type__in);
  const ownerSelectValue = toSingleValue(connectionFilter.owner__in);

  return (
    <div>
      {connectionDetailsModal && <ManageConnectionDetails />}
      <ConnectionsList
        canAddConnection={canAddConnection}
        reloadConnections={connectionsList.loading}
        connections={connectionsList.results}
        roleFilterValue={roleFilter}
        onChangeRole={(selected) => {
          setRoleFilter(selected);
          setOffset(0);
        }}
        roleFilterDisabled={!roleMapLoaded}
        connectionTypes={connectionTypes}
        loadingConnectionTypes={loadingConnectionTypes}
        connectionUrls={connectionUrls}
        loadingConnectionUrls={loadingConnectionUrls}
        organizations={organizations}
        loadingOrganizations={loadingOrganizations}
        onAddConnection={handleAddConnectionPage}
        onViewDetails={handleToggleConnectionDetailsModal}
        onChangeGlobalSearch={handleGlobalSearch}
        onRefresh={() => {
          connectionsList.refresh();
          roleMapQuery.refetch();
        }}
        globalSearchStr={connectionFilter.search}
        ordering={connectionFilter.ordering}
        onChangeSortBy={handleSortBy}
        onChangeEndpoint={handleFilterEndpoint}
        endpointFilterValue={endpointSelectValue}
        onChangeType={handleFilterType}
        typeFilterValue={typeSelectValue}
        onChangeOwner={handleFilterOwner}
        ownerFilterValue={ownerSelectValue}
        onPageClicked={handlePageClick}
        totalCount={connectionsList.count}
        pageLoaded={pageLoaded}
        take={page_size}
        offset={offset}
      />
    </div>
  );
};

export default ConnectionsPage;
